import dayjs from "dayjs";

/** The named date ranges offered across the app, such as in filters and exports. */
export type DateRangePresetKey = "today" | "week" | "month" | "ytd" | "last7" | "last30";

/**
 * Every named date range, worked out when asked so a range stays current. A calendar period covers the whole
 * period, so "This Month" includes days later this month for dates that can be in the future. "To date" and
 * "last N days" ranges end today.
 */
export const DATE_RANGE_PRESETS: Record<DateRangePresetKey, { title: string; start: () => number; end: () => number }> = {
    today: { title: "Today", start: () => dayjs().startOf("day").valueOf(), end: () => dayjs().endOf("day").valueOf() },
    week: { title: "This Week", start: () => dayjs().startOf("week").valueOf(), end: () => dayjs().endOf("week").valueOf() },
    month: { title: "This Month", start: () => dayjs().startOf("month").valueOf(), end: () => dayjs().endOf("month").valueOf() },
    ytd: { title: "Year to Date", start: () => dayjs().startOf("year").valueOf(), end: () => dayjs().endOf("day").valueOf() },
    last7: { title: "Last 7 Days", start: () => dayjs().subtract(7, "day").startOf("day").valueOf(), end: () => dayjs().endOf("day").valueOf() },
    last30: { title: "Last 30 Days", start: () => dayjs().subtract(30, "day").startOf("day").valueOf(), end: () => dayjs().endOf("day").valueOf() }
};

/** Every preset, in the order menus offer them: shortest first, calendar periods beside the rolling window of about the same length. */
export const DATE_RANGE_PRESET_KEYS: readonly DateRangePresetKey[] = ["today", "week", "last7", "month", "last30", "ytd"];

/** The timestamps a date range stands for: a preset worked out as of now, or a custom range as picked. Either end of a custom range may be open. */
export function resolveDateRange(props: { preset?: DateRangePresetKey; start?: number; end?: number }): { start?: number; end?: number } {
    if (props.preset !== undefined) {
        const preset = DATE_RANGE_PRESETS[props.preset];
        return { start: preset.start(), end: preset.end() };
    }
    return { start: props.start, end: props.end };
}
